import type { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { addEditedField } from '../../../../../app/sliceUtils';
import type {
  EstimatedProcedureView,
  DiagnosisView,
  PatientResponsibilityView,
  ApprovalView,
  EstimateEvaluationView,
  ProcedureReimbursementResult,
} from '../../index';
import type { VerificationSliceState } from '../state';

/**
 * Estimate-tab section reducers (spec 006). Each partial-merges into `state.current`, marks the
 * section edited, and keeps the procedures grid coherent (derived total, contiguous priorities). The
 * evaluation reducers store the transient server-evaluated result and never mark the case edited.
 */

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** Re-sequence priorities to 1..n in array order. */
const resequence = (procedures: EstimatedProcedureView[]): void => {
  procedures.forEach((p, i) => {
    p.priority = i + 1;
  });
};

export const addProcedure: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<EstimatedProcedureView> & { code: string; atTop?: boolean }>
> = (state, action) => {
  if (!state.current) return;
  const procs = state.current.procedures;
  const p = action.payload;
  const row: EstimatedProcedureView = {
    priority: procs.length + 1,
    code: p.code,
    description: p.description ?? '',
    billAmount: p.billAmount ?? 0,
    procedureReimbursement: p.procedureReimbursement ?? 0,
    implantReimbursement: p.implantReimbursement ?? 0,
    reimbursement: round2((p.procedureReimbursement ?? 0) + (p.implantReimbursement ?? 0)),
    implantCost: p.implantCost ?? 0,
    selfPay: p.selfPay ?? false,
    isAuthRequired: p.isAuthRequired ?? false,
  };
  // A DRG must occupy the first line: reimbursement (contract DRG rate + DRG filtering) is applied to the
  // top-priority procedure, so a DRG added anywhere else lands the reimbursement on the leading CPT instead
  // (matches the legacy sendToTop-on-DRG behavior and the EVA apply path, which places the DRG at priority 1).
  if (p.atTop) procs.unshift(row);
  else procs.push(row);
  resequence(procs);
  addEditedField(state, 'procedures');
};

export const removeProcedure: CaseReducer<VerificationSliceState, PayloadAction<number>> = (
  state,
  action,
) => {
  if (!state.current) return;
  const procs = state.current.procedures.filter((p) => p.priority !== action.payload);
  state.current.procedures = procs;
  resequence(procs);
  addEditedField(state, 'procedures');
};

export const updateProcedure: CaseReducer<
  VerificationSliceState,
  PayloadAction<{ priority: number; patch: Partial<EstimatedProcedureView> }>
> = (state, action) => {
  if (!state.current) return;
  const proc = state.current.procedures.find((p) => p.priority === action.payload.priority);
  if (!proc) return;
  Object.assign(proc, action.payload.patch);
  // Total Reimb is always derived; it is never directly editable (FR-007).
  proc.reimbursement = round2(proc.procedureReimbursement + proc.implantReimbursement);
  addEditedField(state, 'procedures');
};

export const moveProcedure: CaseReducer<
  VerificationSliceState,
  PayloadAction<{ priority: number; direction: 'up' | 'down' }>
> = (state, action) => {
  if (!state.current) return;
  const procs = state.current.procedures;
  const i = procs.findIndex((p) => p.priority === action.payload.priority);
  if (i < 0) return;
  const j = action.payload.direction === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= procs.length) return;
  [procs[i], procs[j]] = [procs[j], procs[i]];
  resequence(procs);
  addEditedField(state, 'procedures');
};

export const updateDiagnosisCodes: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<DiagnosisView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.diagnosisCodes = { ...state.current.diagnosisCodes, ...action.payload };
  addEditedField(state, 'diagnosisCodes');
};

/**
 * Applies an AI extraction result (spec 028 US5) to the slice in place — replaces the procedures (on
 * `Applied`) and sets Diagnosis Codes 1–4 (on `Applied` + `SkippedRuleFired`), marking both edited. A plain
 * helper (not a slice action) invoked from the `extractProceduresFromNote.matchFulfilled` extra-reducer, so
 * the result enters the slice via the endpoint (never a case refetch) and other unsaved edits (e.g. a pending
 * Check Eligibility benefit load) survive.
 */
export function applyExtractionResult(
  state: VerificationSliceState,
  result: {
    outcome: string;
    procedures: { code: string; description: string; priority: number }[];
    diagnosisCodes: string[];
    unrecognizedProcedureCodes?: string[] | null;
  },
): void {
  if (!state.current) return;

  if (result.outcome === 'Applied') {
    state.current.procedures = [...result.procedures]
      .sort((a, b) => a.priority - b.priority)
      .map((p, i) => ({
        priority: i + 1,
        code: p.code,
        description: p.description ?? '',
        billAmount: 0,
        procedureReimbursement: 0,
        implantReimbursement: 0,
        reimbursement: 0,
        implantCost: 0,
        selfPay: false,
        isAuthRequired: false,
      }));
    addEditedField(state, 'procedures');
    // An absent list means an older backend, not "nothing unrecognized": keep what the case carried.
    if (result.unrecognizedProcedureCodes && state.current.evaDetails) {
      state.current.evaDetails.unrecognizedProcedureCodes = result.unrecognizedProcedureCodes;
    }
  }

  // Diagnosis still applies on a rule-fire skip (the match rules are CPT-only).
  if (result.outcome === 'Applied' || result.outcome === 'SkippedRuleFired') {
    const [d1 = '', d2 = '', d3 = '', d4 = ''] = result.diagnosisCodes;
    state.current.diagnosisCodes = { diagnosis1: d1, diagnosis2: d2, diagnosis3: d3, diagnosis4: d4 };
    addEditedField(state, 'diagnosisCodes');
  }
}

export const updateImplantsNotUsed: CaseReducer<VerificationSliceState, PayloadAction<boolean>> = (
  state,
  action,
) => {
  if (!state.current) return;
  state.current.implantsNotUsed = action.payload;
  addEditedField(state, 'implantsNotUsed');
};

export const updatePatientResponsibility: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<PatientResponsibilityView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.patientResponsibility = {
    ...state.current.patientResponsibility,
    ...action.payload,
  };
  addEditedField(state, 'patientResponsibility');
};

export const updateApproval: CaseReducer<
  VerificationSliceState,
  PayloadAction<Partial<ApprovalView>>
> = (state, action) => {
  if (!state.current) return;
  state.current.approval = { ...state.current.approval, ...action.payload };
  addEditedField(state, 'approval');
};

/**
 * Apply calculator reimbursement results to the working procedures (spec 027). Matches each result to a
 * grid row by **code + priority**, overwrites `procedureReimbursement` (from `FinalAmount`) and
 * `implantReimbursement` (from `ImplantAddOn`), re-derives the total, and marks the case edited so the
 * existing SAVE persists. Rows without a matching result are zeroed (e.g. CPTs dropped by DRG filtering).
 * On the case-rate fallback (`caseRate`, or the calculator returned a single case-level total) the whole
 * `totalReimbursement` is assigned to the priority-1 row and the rest are zeroed. Never persists; holds
 * no reimbursement math.
 */
export const applyReimbursementResults: CaseReducer<
  VerificationSliceState,
  PayloadAction<{
    results: ProcedureReimbursementResult[];
    totalReimbursement: number;
    caseRate: boolean;
  }>
> = (state, action) => {
  if (!state.current) return;
  const { results, totalReimbursement, caseRate } = action.payload;
  const procs = state.current.procedures;

  if (caseRate) {
    procs.forEach((p) => {
      p.procedureReimbursement = p.priority === 1 ? round2(totalReimbursement) : 0;
      p.implantReimbursement = 0;
      p.reimbursement = round2(p.procedureReimbursement + p.implantReimbursement);
    });
    addEditedField(state, 'procedures');
    return;
  }

  procs.forEach((p) => {
    const match = results.find((r) => r.code === p.code && r.priority === p.priority);
    p.procedureReimbursement = match ? round2(match.procedureReimbursement) : 0;
    p.implantReimbursement = match ? round2(match.implantReimbursement) : 0;
    p.reimbursement = round2(p.procedureReimbursement + p.implantReimbursement);
  });
  addEditedField(state, 'procedures');
};

// ── Transient evaluation state (presentational; never marks edited / never persisted) ──

export const setEstimateEvaluation: CaseReducer<
  VerificationSliceState,
  PayloadAction<EstimateEvaluationView>
> = (state, action) => {
  state.evaluation = action.payload;
};

export const clearEstimateEvaluation: CaseReducer<VerificationSliceState> = (state) => {
  state.evaluation = null;
};

export const setDisableCodeSetMatching: CaseReducer<
  VerificationSliceState,
  PayloadAction<boolean>
> = (state, action) => {
  state.disableCodeSetMatching = action.payload;
};
